import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthContext';
import ProtectedRoute from './ProtectedRoute';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
  const { isAdmin, profileLoading, userRole } = useUser();
  const { isAuthenticated } = useAuth();

    // First, use ProtectedRoute to handle authentication
  // This ensures login flow happens before admin check
  if (!isAuthenticated) {
    console.log('User not authenticated, showing login');
    return <ProtectedRoute>{children}</ProtectedRoute>;
  }

  // If authenticated but still loading the profile, show loading indicator
  if (profileLoading) {
    console.log('Profile still loading, showing loading indicator');
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="body1" color="text.secondary">
          Checking permissions...
        </Typography>
      </Box>
    );
  }

  // CRITICAL: Only check admin status if we have received the userRole from the API
  // This ensures we don't make decisions based on the default isAdmin value
  if (isAuthenticated && userRole !== null) {
    // If authenticated and we have the role, but not an admin, redirect to access denied
    if (!isAdmin) {
      console.log('User not admin, redirecting to access denied');
      return <Navigate to="/access-denied" replace />;
    }
    
    // If authenticated and admin, render the protected route with children
    console.log('User is admin, allowing access');
    return <ProtectedRoute>{children}</ProtectedRoute>;
  }

  // If we're still waiting for the role to be fetched, show loading
  console.log('Waiting for user role to be fetched...');
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
    >
      <CircularProgress size={60} sx={{ mb: 3 }} />
      <Typography variant="body1" color="text.secondary">
        Checking permissions...
      </Typography>
    </Box>
  );
};

export default AdminProtectedRoute;